import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom'; 
import {
    QrCode,
    Receipt,
    CheckCircle
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { fetchCaissierTerminal } from '../../services/posService';
import { createPaymentRequest, extractPaymentRequestId } from '../../services/paymentRequestService';
import WaitingPaymentModal from '../../components/payment/WaitingPaymentModal';
import ProviderPicker from '../../components/payment/ProviderPicker';
import PaymentRequestAside from '../../components/payment/PaymentRequestAside';
import { parseAmount, normalizeAmountForApi, formatAmountInputDisplay } from '../../utils/amount';
import AmountInput from '../../components/common/AmountInput';
import { fetchProviders } from '../../services/providerService';
import { PAYMENT_REQUEST_VALIDITY_MINUTES } from '../../config/environment';
import CashierSidebar from '../../components/cashier/CashierSidebar';
import CashierHeader from '../../components/cashier/CashierHeader';

const CashierPaymentRequestsPage = () => {
    const { selectedOrganization, user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [fromCustomer, setFromCustomer] = useState(null);
    const [selectedPos, setSelectedPos] = useState(null);
    const [caissierTerminalLoading, setCaissierTerminalLoading] = useState(false);

    const formatCurrency = (amount, currency) =>
        new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currency || 'CDF' }).format(amount);

    const [title, setTitle] = useState('Règlement');
    const [amount, setAmount] = useState('');
    const [formCurrency, setFormCurrency] = useState('CDF');
    const [providers, setProviders] = useState([]);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [account, setAccount] = useState('');
    const [accountFormatError, setAccountFormatError] = useState(false);
    const [loadingProviders, setLoadingProviders] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState('');
    const [paymentRequest, setPaymentRequest] = useState(null);
    const [waitingModalOpen, setWaitingModalOpen] = useState(false);
    const [waitingRequestId, setWaitingRequestId] = useState(null);
    const accountRef = useRef(null);
    const previousValue = useRef('');

    useEffect(() => {
        if (selectedOrganization?.id) {
            loadCaissierTerminal();
            loadProviders();
        }
    }, [selectedOrganization?.id, user?.id]);

    useEffect(() => {
        if (selectedProvider?.id) {
            setAccount('');
            setAccountFormatError(false);
            previousValue.current = '';
        }
    }, [selectedProvider?.id]);

    useEffect(() => {
        if (account && selectedProvider) {
            const accountLength = selectedProvider.account_length;
            const accountFormat = selectedProvider.account_format;
            if (account.length === accountLength && accountFormat) {
                try {
                    const regex = new RegExp(accountFormat);
                    setAccountFormatError(!regex.test(account));
                } catch {
                    setAccountFormatError(false);
                }
            } else {
                setAccountFormatError(false);
            }
        }
    }, [account, selectedProvider]);

    const loadCaissierTerminal = async () => {
        if (!selectedOrganization?.id || !user?.id) return;
        try {
            setCaissierTerminalLoading(true);
            const terminal = await fetchCaissierTerminal(selectedOrganization.id, user.id);
            setSelectedPos(terminal || null);
        } catch (error) {
            console.error('Error loading caissier terminal:', error);
            setSelectedPos(null);
        } finally {
            setCaissierTerminalLoading(false);
        }
    };

    const loadProviders = async () => {
        try {
            setLoadingProviders(true);
            const response = await fetchProviders({ typ: 'all', opr: 'PMT' });
            if (response?.contents?.length) {
                setProviders(response.contents);
                if (!selectedProvider) setSelectedProvider(response.contents[0]);
            }
        } catch (error) {
            console.error('Error loading providers:', error);
        } finally {
            setLoadingProviders(false);
        }
    };

    const formatAccountNumber = (input) => {
        const raw = input.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        if (raw.length >= 2 && raw.length < 6) return raw.substring(0, 2) + '-' + raw.substring(2);
        if (raw.length >= 6 && raw.length < 9) return raw.substring(0, 2) + '-' + raw.substring(2, 6) + '-' + raw.substring(6);
        if (raw.length >= 9) return raw.substring(0, 2) + '-' + raw.substring(2, 6) + '-' + raw.substring(6, 9);
        return raw;
    };

    const handleAccountChange = (text) => {
        if (selectedProvider?.provider_type?.toLowerCase() === 'netikash') {
            const isDeleting = text.length < (previousValue.current?.length ?? 0);
            if (!isDeleting) text = formatAccountNumber(text);
            previousValue.current = text;
            setAccount(text);
        } else {
            let cleaned = text.replace(/\D/g, '');
            if (selectedProvider?.provider_type?.toUpperCase() === 'MOBILE MONEY' && cleaned.startsWith('243')) cleaned = cleaned.substring(3);
            if (selectedProvider?.account_length && cleaned.length > selectedProvider.account_length) cleaned = cleaned.substring(0, selectedProvider.account_length);
            setAccount(cleaned);
        }
    };

    const handleProviderSelect = (provider) => {
        setSelectedProvider(provider);
        setAccount('');
        setAccountFormatError(false);
        previousValue.current = '';
    };

    useEffect(() => {
        const customer = location.state?.customer;
        if (customer) {
            setFromCustomer(customer);
            const phone = customer.phoneNumber || (customer.phone ? String(customer.phone).replace(customer.zip || '243', '') : '');
            if (phone) setAccount(phone.replace(/\D/g, ''));
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state?.customer, location.pathname, navigate]);

    const handleCreatePaymentRequest = async () => {
        try {
            const amountNum = normalizeAmountForApi(amount, formCurrency);
            if (amountNum == null || amountNum <= 0) {
                setFormError('Le montant est vide ou inférieur à zéro');
                return;
            }
            if (!selectedProvider?.id || !account) {
                setFormError('Veuillez indiquer le numéro de compte et sélectionner un réseau');
                return;
            }
            if (!selectedOrganization?.id) {
                setFormError('Organisation non sélectionnée');
                return;
            }
            if (!selectedPos?.id) {
                setFormError('Aucun terminal assigné à votre compte. Contactez l\'administrateur.');
                return;
            }
            const pos = selectedPos;

            setSubmitting(true);
            setFormError('');

            let cleanedAccount = account;
            let accountPrefix = null;
            if (selectedProvider.provider_type?.toLowerCase() === 'netikash') {
                cleanedAccount = account.replace(/-/g, '').toUpperCase();
            } else if (selectedProvider.provider_type?.toUpperCase() === 'MOBILE MONEY') {
                cleanedAccount = account.replace(/\D/g, '');
                if (cleanedAccount.startsWith('243') && cleanedAccount.length > 3) cleanedAccount = cleanedAccount.substring(3);
                if (cleanedAccount.length > 0) accountPrefix = '243';
            }

            const customerPayload = fromCustomer ? {
                zip: fromCustomer.zip || '243',
                phoneNumber: (fromCustomer.phoneNumber ? String(fromCustomer.phoneNumber).replace(/\D/g, '') : cleanedAccount) || cleanedAccount,
                name: fromCustomer.name || null,
                email: fromCustomer.email || null,
                address: fromCustomer.address || null,
                client_type: fromCustomer.client_type || fromCustomer.clientType || 'PP',
            } : null;
            const paymentRequestData = {
                title: title || 'Règlement',
                amount: amountNum,
                currency: formCurrency,
                company_id: selectedOrganization.id,
                terminal_id: pos.id,
                customer: customerPayload,
                paymentMethod: {
                    prefix: accountPrefix,
                    provider_id: selectedProvider.id,
                    provider_name: selectedProvider.name,
                    suffix: null,
                    account: cleanedAccount,
                    full_name: null,
                    expiration_date: null,
                    control: null,
                    owner: null,
                }
            };

            const response = await createPaymentRequest(paymentRequestData);
            if (response) {
                const { error: apiError, message, payload } = response;
                if (apiError) {
                    setFormError(message || 'Erreur lors de la création de la demande');
                    setSubmitting(false);
                    return;
                }
                const requestId = extractPaymentRequestId(response);
                setPaymentRequest({ ...payload, request_id: requestId });
                setTitle('Règlement');
                setAmount('');
                setAccount('');
                setFormError('');
                window.dispatchEvent(new CustomEvent('paymentRequest:created', { detail: { organizationId: selectedOrganization.id } }));
                if (requestId) {
                    setWaitingRequestId(requestId);
                    setWaitingModalOpen(true);
                }
            }
        } catch (error) {
            console.error('Error creating payment request:', error);
            let errorMessage = 'Erreur lors de la création de la demande de paiement';
            if (error?.response?.status === 500) errorMessage = 'Erreur serveur. Veuillez réessayer.';
            else if (error?.response?.data?.message) errorMessage = error.response.data.message;
            else if (error?.message && !error.message.includes('Internal Server Error')) errorMessage = error.message;
            setFormError(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50/20 to-slate-50 flex">
            <CashierSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />

            <div className="flex-1 min-w-0 min-h-screen transition-all duration-300 overflow-hidden flex flex-col lg:ml-[var(--cashier-sidebar-w,18rem)] transition-[margin] duration-300">
                <CashierHeader
                    onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
                    onRefresh={() => { loadCaissierTerminal(); loadProviders(); }}
                    loading={caissierTerminalLoading || loadingProviders}
                    terminal={selectedPos}
                />

                <div className="flex-1 p-5 lg:p-8 overflow-y-auto">
                    {/* Bannière */}
                    <div className="mb-8">
                        <div className="relative bg-white/90 backdrop-blur-sm rounded-3xl border border-amber-200/50 shadow-xl shadow-amber-500/5 overflow-hidden">
                            <div className="absolute inset-0 bg-gradient-to-r from-amber-400/10 via-orange-400/5 to-transparent pointer-events-none" />
                            <div className="relative px-6 lg:px-8 py-6">
                                <div className="flex items-center gap-5">
                                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30 ring-4 ring-amber-500/10">
                                        <Receipt className="text-white text-3xl" />
                                    </div>
                                    <div>
                                        <h1 className="text-2xl lg:text-3xl font-bold text-slate-800 tracking-tight">Demandes de paiement</h1>
                                        <p className="text-slate-600 mt-1">Créez des demandes et suivez les paiements envoyés à vos clients</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-8">
                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 items-start">
                            {/* Colonne gauche : formulaire */}
                            <div className="xl:col-span-2">
                                <div className="bg-white/95 backdrop-blur-sm rounded-3xl border border-slate-200/80 shadow-xl shadow-slate-200/50 overflow-hidden">
                                    <div className="px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-amber-50/80 to-orange-50/50">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
                                                <Receipt className="text-white text-xl" />
                                            </div>
                                            <div>
                                                <h2 className="text-xl font-bold text-slate-800">Nouvelle demande</h2>
                                                <p className="text-sm text-slate-600 mt-0.5">Montant, réseau et numéro de compte</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-6">
                                        <div className="mb-5">
                                            <div className="flex items-center justify-between mb-2">
                                                <label className="block text-sm font-semibold text-slate-700">Montant ({formCurrency})</label>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const next = formCurrency === 'CDF' ? 'USD' : 'CDF';
                                                        setFormCurrency(next);
                                                        if (amount) setAmount(formatAmountInputDisplay(amount, next));
                                                    }}
                                                    className="text-sm font-semibold text-amber-600 hover:text-amber-700 transition-colors"
                                                >
                                                    {formCurrency === 'CDF' ? '↔ USD' : '↔ CDF'}
                                                </button>
                                            </div>
                                            <div className="flex gap-3">
                                                <AmountInput
                                                    value={amount}
                                                    onChange={setAmount}
                                                    currency={formCurrency}
                                                    placeholder="Ex: 15000"
                                                    className="flex-1 px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-400 focus:border-amber-400 transition-all bg-slate-50/50"
                                                />
                                                <div className="px-5 py-3 rounded-xl bg-amber-50 border-2 border-amber-200 text-amber-800 font-semibold text-center min-w-[88px] flex items-center justify-center">{formCurrency}</div>
                                            </div>
                                        </div>
                                        <div className="mb-5">
                                            <label className="block text-sm font-semibold text-slate-700 mb-2">Message (optionnel)</label>
                                            <textarea value={title} onChange={(e) => setTitle(e.target.value)} className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-400 focus:border-amber-400 min-h-[88px] bg-slate-50/50 transition-all placeholder:text-slate-400" placeholder="Note pour le client" />
                                        </div>
                                        <div className="mb-5">
                                            <label className="block text-sm font-semibold text-slate-700 mb-3">Réseau de paiement *</label>
                                            <ProviderPicker
                                                providers={providers}
                                                selectedId={selectedProvider?.id}
                                                onSelect={handleProviderSelect}
                                                loading={loadingProviders}
                                            />
                                        </div>
                                        {selectedProvider && (
                                            <div className="mb-5">
                                                <label className="block text-sm font-semibold text-slate-700 mb-2">{selectedProvider.account_label || 'Numéro de compte'} *</label>
                                                <input
                                                    ref={accountRef}
                                                    type={selectedProvider.provider_type?.toLowerCase() === 'netikash' ? 'text' : 'tel'}
                                                    inputMode={selectedProvider.provider_type?.toLowerCase() === 'netikash' ? 'text' : 'numeric'}
                                                    autoComplete="off"
                                                    value={account}
                                                    onChange={(e) => handleAccountChange(e.target.value)}
                                                    className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-amber-400 transition-all bg-slate-50/50 ${accountFormatError ? 'border-rose-400 bg-rose-50/50 focus:border-rose-400' : 'border-slate-200 focus:border-amber-400'}`}
                                                    placeholder={selectedProvider.account_placeholder || 'Numéro de compte'}
                                                    maxLength={selectedProvider.account_length ? (selectedProvider.provider_type?.toLowerCase() === 'netikash' ? selectedProvider.account_length + 2 : selectedProvider.account_length) : undefined}
                                                />
                                                {accountFormatError && <p className="mt-2 text-sm text-rose-600 font-medium">Veuillez utiliser le bon format du {selectedProvider.account_label}</p>}
                                                {selectedProvider.provider_type?.toUpperCase() === 'MOBILE MONEY' && <p className="mt-1.5 text-xs text-slate-500">Numéro uniquement (ex: 999123456). Le préfixe +243 est ajouté automatiquement.</p>}
                                            </div>
                                        )}
                                        <div className="mb-5">
                                            <label className="block text-sm font-semibold text-slate-700 mb-2">Terminal de paiement</label>
                                            {caissierTerminalLoading ? (
                                                <div className="w-full bg-slate-50 border-2 border-slate-200 rounded-xl px-4 py-3.5 flex items-center gap-3">
                                                    <div className="w-5 h-5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                                                    <span className="text-slate-600 text-sm">Chargement de votre terminal...</span>
                                                </div>
                                            ) : selectedPos ? (
                                                <div className="w-full bg-gradient-to-r from-amber-50 to-orange-50/50 border-2 border-amber-200/60 rounded-xl px-4 py-3.5">
                                                    <p className="font-semibold text-slate-800">{selectedPos.name || `Terminal ${selectedPos.tag || selectedPos.id}`}</p>
                                                    {(selectedPos.tag || selectedPos.description) && (
                                                        <p className="text-sm text-slate-600 mt-0.5">{selectedPos.tag || selectedPos.description}</p>
                                                    )}
                                                    <p className="text-xs text-slate-500 mt-2">Attribué à votre compte. La modification est réservée à l&apos;administrateur.</p>
                                                </div>
                                            ) : (
                                                <div className="w-full bg-amber-50/80 border-2 border-amber-200 rounded-xl px-4 py-3.5">
                                                    <p className="text-amber-800 font-medium">Aucun terminal assigné à votre compte.</p>
                                                    <p className="text-amber-700/90 text-sm mt-1">Contactez l&apos;administrateur pour qu&apos;il vous assigne un terminal.</p>
                                                </div>
                                            )}
                                        </div>
                                        {formError && <div className="mb-5 bg-rose-50 border-2 border-rose-200 text-rose-700 px-4 py-3 rounded-xl text-sm font-medium">{formError}</div>}
                                        <div className="flex gap-3">
                                            <button
                                                onClick={handleCreatePaymentRequest}
                                                disabled={submitting || !amount || !selectedProvider || !account || !selectedPos}
                                                className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3.5 px-5 rounded-xl font-semibold hover:from-amber-600 hover:to-orange-600 transition-all duration-300 shadow-lg shadow-amber-500/25 hover:shadow-xl hover:shadow-amber-500/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                                            >
                                                {submitting ? <div className="flex items-center justify-center gap-2"><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /><span>Création...</span></div> : <><QrCode className="inline mr-2 text-lg" />Envoyer la demande</>}
                                            </button>
                                            <button type="button" onClick={() => { setTitle('Règlement'); setAmount(''); setAccount(''); setFormError(''); setPaymentRequest(null); }} className="px-6 py-3.5 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 border border-slate-200 transition-all">Effacer</button>
                                        </div>
                                    </div>
                                </div>

                                <WaitingPaymentModal
                                    open={waitingModalOpen}
                                    requestId={waitingRequestId}
                                    amount={paymentRequest?.amount ?? (parseAmount(amount) || '—')}
                                    currency={paymentRequest?.currency || formCurrency}
                                    onClose={() => {
                                        setWaitingModalOpen(false);
                                        setWaitingRequestId(null);
                                    }}
                                    onApproved={() => {
                                        window.dispatchEvent(new CustomEvent('transaction:created', {
                                            detail: { organizationId: selectedOrganization?.id }
                                        }));
                                    }}
                                />

                                {paymentRequest && !waitingModalOpen && (
                                    <div className="mt-8 bg-gradient-to-r from-emerald-50 to-teal-50/50 border-2 border-emerald-200/80 rounded-2xl p-6 shadow-lg shadow-emerald-500/10">
                                        <div className="flex items-start gap-5">
                                            <div className="w-14 h-14 rounded-2xl bg-emerald-500 flex items-center justify-center shrink-0 shadow-lg shadow-emerald-500/30"><CheckCircle className="text-white text-2xl" /></div>
                                            <div className="min-w-0 flex-1">
                                                <h3 className="text-lg font-bold text-emerald-900 mb-1">Demande envoyée</h3>
                                                <p className="text-sm text-emerald-800/90 mb-5">La demande a été envoyée au client. Une notification a été envoyée sur son wallet pour confirmer le paiement.</p>
                                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                                                    <div className="bg-white/80 rounded-xl px-4 py-3 border border-emerald-100 shadow-sm">
                                                        <span className="text-emerald-700 block text-xs font-semibold uppercase tracking-wide">Montant</span>
                                                        <span className="font-bold text-slate-900 mt-0.5">{formatCurrency(paymentRequest?.amount ?? parseAmount(amount), paymentRequest?.currency || formCurrency)}</span>
                                                    </div>
                                                    <div className="bg-white/80 rounded-xl px-4 py-3 border border-emerald-100 shadow-sm">
                                                        <span className="text-emerald-700 block text-xs font-semibold uppercase tracking-wide">Client</span>
                                                        <span className="font-semibold text-slate-800 truncate block mt-0.5">{paymentRequest?.customer?.name || paymentRequest?.paymentMethod?.account || account || '—'}</span>
                                                    </div>
                                                    <div className="bg-white/80 rounded-xl px-4 py-3 border border-emerald-100 shadow-sm">
                                                        <span className="text-emerald-700 block text-xs font-semibold uppercase tracking-wide">Réseau</span>
                                                        <span className="font-semibold text-slate-800 mt-0.5">{paymentRequest?.paymentMethod?.provider_name || selectedProvider?.name || '—'}</span>
                                                    </div>
                                                    <div className="bg-white/80 rounded-xl px-4 py-3 border border-emerald-100 shadow-sm">
                                                        <span className="text-emerald-700 block text-xs font-semibold uppercase tracking-wide">Valide {PAYMENT_REQUEST_VALIDITY_MINUTES} min</span>
                                                        <span className="font-semibold text-slate-800 mt-0.5">En attente</span>
                                                    </div>
                                                </div>
                                                <button type="button" onClick={() => setPaymentRequest(null)} className="mt-5 text-sm font-semibold text-emerald-700 hover:text-emerald-800 hover:underline">Créer une autre demande</button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Colonne droite : aide */}
                            <PaymentRequestAside providers={providers} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CashierPaymentRequestsPage;
